import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import useSearchCompany from "../../../../Hooks/useSearchCompany";
import {
  DEFAULT_SEARCH_COUNTRY,
  SEARCH_DEBOUNCE_MS,
  canSearch,
} from "./ownershipSearch";

/* The business search behind a corporate owner. */
export const useOwnershipSearch = ({
  defaultCountry = DEFAULT_SEARCH_COUNTRY,
} = {}) => {
  const { token } = useSelector((state) => state.auth);
  const { worldBoxToken } = useSelector((state) => state.search);

  /* What the viewer has typed, and the debounced value actually queried. */
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  /* The country is NOT optional to this search, however it looks: `search_new` rejects a request without one outright (400), and past that guard it filters... */
  const [country, setCountry] = useState(defaultCountry);
  const timer = useRef(null);

  /* A late-arriving default (the company query resolves after mount) still
     seeds the field, but never overwrites a country the viewer has chosen. */
  useEffect(() => {
    if (defaultCountry) setCountry((current) => current || defaultCountry);
  }, [defaultCountry]);

  const { data, isLoading, error, message } = useSearchCompany(
    query,
    country,
    token,
    worldBoxToken
  );

  /* Transcribed: below three characters the results are cleared rather than
     left showing the previous term's hits. */
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);

    if (!canSearch(term)) {
      setQuery("");
      return undefined;
    }

    timer.current = setTimeout(() => setQuery(term), SEARCH_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [term]);

  return {
    term,
    setTerm,
    country,
    setCountry,
    /* An un-queried term must not show the previous term's results. */
    results: canSearch(term) ? data || [] : [],
    isLoading,
    error,
    message,
  };
};

export default useOwnershipSearch;
